import React from "react";
import { Link } from "react-router-dom";
import { BookOpen, IndianRupee, BadgeCheck, ExternalLink, Clock, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { useData } from "../DataContext";
import { usePageMeta } from "../hooks/usePageMeta";
import JsonLd from "../components/JsonLd";
import { buildWebPageLD, buildBreadcrumbLD } from "../lib/jsonld";

const buildSections = (RESOURCES) => [
  { id: "brochures", title: "Brochures", icon: BookOpen, intro: "Browse the complete product catalogues online or download them for offline reference.", items: RESOURCES.brochures },
  { id: "price-lists", title: "Price Lists", icon: IndianRupee, intro: "Latest dealer price lists across all BAISHALII product ranges.", items: RESOURCES.priceLists },
  { id: "certifications", title: "Certifications", icon: BadgeCheck, intro: "Quality certifications and test reports that back every BAISHALII product.", items: RESOURCES.certifications }
];

const ResourceCard = ({ item, sectionId }) => (
  <div
    data-testid={`resource-card-${sectionId}-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    className="bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-lg transition-shadow p-6 flex flex-col"
  >
    <h3 className="text-lg font-black text-[#0a2a5e]">{item.title}</h3>
    <p className="text-sm text-slate-500 mt-2 flex-1">{item.desc}</p>
    <div className="mt-5 flex flex-wrap gap-2">
      {item.viewUrl && (
        <Button asChild className="bg-gradient-to-r from-sky-500 to-[#0a2a5e] hover:opacity-90 text-white rounded-full px-5">
          <a href={item.viewUrl} target="_blank" rel="noopener noreferrer" data-testid={`resource-view-${sectionId}`}>
            View Online <ExternalLink className="h-4 w-4 ml-1.5" />
          </a>
        </Button>
      )}
      {item.downloadUrl && (
        <Button asChild variant="outline" className="rounded-full px-5 border-[#0a2a5e] text-[#0a2a5e] hover:bg-sky-50">
          <a href={item.downloadUrl} download data-testid={`resource-download-${sectionId}`}>
            Download <Download className="h-4 w-4 ml-1.5" />
          </a>
        </Button>
      )}
      {!item.viewUrl && !item.downloadUrl && (
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 bg-slate-50 border border-slate-100 rounded-full px-4 py-2">
          <Clock className="h-3.5 w-3.5" /> Coming Soon
        </span>
      )}
    </div>
  </div>
);

export default function Resources() {
  usePageMeta(
    "Resources — Brochures, Price Lists & Certifications",
    "Download BAISHALII product brochures, latest dealer price lists and quality certifications for tanks, pipes, sinks and more."
  );
  const { RESOURCES } = useData();
  const SECTIONS = buildSections(RESOURCES);
  return (
    <div className="pt-28 md:pt-32" data-testid="resources-page">
      <JsonLd blocks={[
        buildWebPageLD({ name: "BAISHALII Resources", path: "/resources", description: "Download BAISHALII product brochures, latest dealer price lists and quality certifications." }),
        buildBreadcrumbLD([{ name: "Home", path: "/" }, { name: "Resources", path: "/resources" }]),
      ]} id="resources-page" />
      {/* hero */}
      <section className="bg-[#0a2a5e] text-white">
        <div className="max-w-7xl mx-auto px-6 py-14 md:py-20">
          <nav className="text-xs text-sky-300 mb-4">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span className="mx-2 text-sky-500">/</span>
            <span className="text-white">Resources</span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black">Resources</h1>
          <p className="text-base md:text-lg text-sky-100 mt-4 max-w-2xl">
            Certifications, price lists and product brochures — everything you need about BAISHALII products in one place.
          </p>
        </div>
      </section>

      {/* sections */}
      {SECTIONS.map((s, idx) => (
        <section key={s.id} id={s.id} className={idx % 2 === 0 ? "bg-slate-50" : "bg-white"}>
          <div className="max-w-7xl mx-auto px-6 py-14 md:py-16">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center">
                <s.icon className="h-5 w-5" />
              </div>
              <h2 className="text-lg font-black text-[#0a2a5e] uppercase tracking-wide">{s.title}</h2>
            </div>
            <p className="text-sm text-slate-500 mb-8 max-w-2xl">{s.intro}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {s.items.map((item) => (
                <ResourceCard key={item.title} item={item} sectionId={s.id} />
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="bg-[#0a2a5e]">
        <div className="max-w-7xl mx-auto px-6 py-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-lg md:text-xl font-black text-white">Need a specific document?</h2>
            <p className="text-sm text-sky-200 mt-1">Write to us and we'll send it across right away.</p>
          </div>
          <Button asChild className="bg-white text-[#0a2a5e] hover:bg-sky-100 rounded-full px-6 font-bold">
            <Link to="/contact" data-testid="resources-contact-cta">Contact Us</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
